import {
  AUTOCOMPLETE_DEBOUNCE_MS,
  isEmptyOrSpaces,
  MINIMUM_AUTOCOMPLETE_QUERY_LENGTH,
} from 'spa/shared/utils'
import {
  autocompleteSearch,
  clearAutocompleteSongs,
} from 'spa/action_creators/autocomplete'
import { defaultFilters, defaultSortBy } from 'spa/api/songs'
import { defineMessages, injectIntl } from 'react-intl'
import { getQueryParams } from 'spa/shared/utils'
import { SearchBox } from 'spa/components/search_box'
import { useDispatch, useSelector } from 'react-redux'
import { useHistory, useLocation } from 'react-router-dom'
import debounce from 'lodash/debounce'
import emojiRegex from 'emoji-regex'
import PropTypes from 'prop-types'
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'

const localizeMessages = defineMessages({
  search: {
    id: 'djcity.common.site.banner.search',
    defaultMessage: 'Search artists, titles, producers...',
  },
})

const buildSearchURL = q => `/search-overview?${getQueryParams({ q })}`

const buildRemixerURL = r => `/remixer/${r.slug}`

const buildTagURL = t => `/genre/${t.slug}`

const buildPlaylistURL = playlist =>
  `/playlists/${playlist.groupSlug}/#${playlist.slug}`

function SongSearchBox({ className, intl, reset }) {
  const history = useHistory()
  const location = useLocation()
  const dispatch = useDispatch()
  const songs = useSelector(state => state.autocompleteSongs.suggestions)

  // The text field states: current value and whether
  // it has focus or not
  const [value, setValue] = useState('')
  const [focus, setFocus] = useState(false)
  const inputRef = useRef()

  const onChange = useCallback(e => setValue(e.target.value), [setValue])
  const onFocus = useCallback(() => setFocus(true), [setFocus])
  const onBlur = useCallback(() => setFocus(false), [setFocus])
  const onReset = useCallback(() => {
    setValue('')
    if (typeof reset === 'function') {
      reset()
    }
  }, [setValue])

  const removeEmojis = (str = '') => {
    const regex = emojiRegex()
    return str.replace(regex, '')
  }

  // The function that searches songs
  const searchSuggestions = useCallback(
    debounce(searchTerm => {
      const sanitizedSearchTerm = removeEmojis(searchTerm)
      if (
        !isEmptyOrSpaces(sanitizedSearchTerm) &&
        sanitizedSearchTerm.trim().length >= MINIMUM_AUTOCOMPLETE_QUERY_LENGTH
      ) {
        dispatch(
          autocompleteSearch({
            filters: defaultFilters,
            searchTerm: sanitizedSearchTerm,
            sortBy: defaultSortBy,
          })
        )
      }
    }, AUTOCOMPLETE_DEBOUNCE_MS),
    [dispatch]
  )

  // Trigger a search when the user types something or clear the search results
  // if the query term is small
  useEffect(() => {
    if (removeEmojis(value).trim().length < MINIMUM_AUTOCOMPLETE_QUERY_LENGTH) {
      searchSuggestions.cancel()
      dispatch(clearAutocompleteSongs())
      return
    }
    searchSuggestions(value)
  }, [value, searchSuggestions])

  // The suggestions list
  const suggestions = useMemo(() => {
    const results = []
    if (
      focus &&
      removeEmojis(value).trim().length >= MINIMUM_AUTOCOMPLETE_QUERY_LENGTH
    ) {
      const sections = [
        { category: 'genre', title: 'Genres' },
        { category: 'artist', title: 'Artists' },
        { category: 'track', title: 'Tracks' },
        { category: 'remixer', title: 'Remixers' },
        { category: 'playlist', title: 'Playlists' },
      ]

      sections.forEach(section => {
        const matches = (songs || []).filter(
          suggestion => suggestion.data.category === section.category
        )
        if (!matches.length) return

        results.push({
          title: section.title,
          suggestions: matches.map(suggestion => {
            let url = buildSearchURL(suggestion.value)
            if (section.category === 'genre' && suggestion.data.slug) {
              url = buildTagURL({ slug: suggestion.data.slug })
            } else if (section.category === 'remixer' && suggestion.data.slug) {
              url = buildRemixerURL({ slug: suggestion.data.slug })
            } else if (
              section.category === 'playlist' &&
              suggestion.data.groupSlug &&
              suggestion.data.slug
            ) {
              url = buildPlaylistURL(suggestion.data)
            }
            return { value: suggestion.value, url }
          }),
        })
      })
    }

    return results
  }, [value, focus, songs])

  // What to do when the user hits "Enter" to submit a search
  const onSubmit = useCallback(
    query => {
      const sanitizedQuery = removeEmojis(query)
      if (sanitizedQuery.trim().length >= MINIMUM_AUTOCOMPLETE_QUERY_LENGTH) {
        if (inputRef.current) {
          inputRef.current.blur()
        }
        if (reset) reset()
        history.push(buildSearchURL(sanitizedQuery))
      }
    },
    [inputRef.current, history]
  )

  // What to do when a selection is made
  const onSuggestionSelected = useCallback(
    suggestion => {
      if (inputRef.current) {
        inputRef.current.blur()
      }
      if (reset) reset()
      history.push(suggestion.url)
    },
    [inputRef.current, history]
  )

  // When navigation occurs, clear the search box
  useEffect(() => {
    setValue('')
  }, [location, setValue])

  return (
    <SearchBox
      className={className}
      id="songSearchBox"
      inputRef={inputRef}
      isFocused={focus}
      onBlur={onBlur}
      onChange={onChange}
      onFocus={onFocus}
      onSubmit={onSubmit}
      onSuggestionSelected={onSuggestionSelected}
      placeholder={intl.formatMessage(localizeMessages.search)}
      reset={onReset}
      showActions={true}
      suggestions={suggestions}
      value={value}
    />
  )
}

SongSearchBox.propTypes = {
  className: PropTypes.string,
  intl: PropTypes.shape({
    formatMessage: PropTypes.func,
  }).isRequired,
  reset: PropTypes.func,
}

const LocalizedSongSearchBox = injectIntl(SongSearchBox)

export { LocalizedSongSearchBox as SongSearchBox }
